import type { FxCurrency } from "@trader/shared/FxApi"
import { CurrencyCode, formatDecimal, IsoDate } from "@trader/shared/Money"
import { Schema } from "effect"
import { useEffect, useState } from "react"
import { call } from "../../api.ts"
import { createFxSeriesCache, type FxFixingSeries } from "./fxSeriesCache.ts"

// FX rates for backtests in another currency, fetched through api.ts: the
// currencies the server keeps (Properties › Account currency) and per-pair
// series, cached (fxSeriesCache.ts).

const decodeCode = Schema.decodeUnknownSync(CurrencyCode)
const decodeDate = Schema.decodeUnknownSync(IsoDate)

const fetchSeries = async (from: string, to: string, start: string, end: string): Promise<FxFixingSeries> => {
  const query = { from: decodeCode(from), to: decodeCode(to), start: decodeDate(start), end: decodeDate(end) }
  const series = await call((client) => client.fx.series({ query }))
  return {
    from: series.from,
    to: series.to,
    source: series.source,
    derivedVia: series.derivedVia,
    dates: series.dates,
    // Exact decimals on the wire; the report is analysis, so numbers from here on.
    rates: series.rates.map((rate) => Number(formatDecimal(rate)))
  }
}

/** Per-pair FX series, shared by every pane. */
export const fxSeries = createFxSeriesCache(fetchSeries)

const CURRENCIES_TTL_MS = 10 * 60_000
let currencies: { readonly at: number; readonly list: Promise<ReadonlyArray<FxCurrency>> } | null = null

/** The currencies the server has FX rates for (cached for a few minutes; a failure is retried next time). */
export const fxCurrencies = (): Promise<ReadonlyArray<FxCurrency>> => {
  if (currencies !== null && Date.now() - currencies.at < CURRENCIES_TTL_MS) return currencies.list
  const list = call((client) => client.fx.currencies())
  const entry = { at: Date.now(), list }
  list.catch(() => {
    if (currencies === entry) currencies = null
  })
  currencies = entry
  return list
}

/** The FX currencies for a form: null while loading, or when they can't be loaded. */
export const useFxCurrencies = (): ReadonlyArray<FxCurrency> | null => {
  const [list, setList] = useState<ReadonlyArray<FxCurrency> | null>(null)
  useEffect(() => {
    let live = true
    fxCurrencies().then(
      (loaded) => {
        if (live) setList(loaded)
      },
      () => {
        // Offline or signed out: the form offers the symbol's currency only.
      }
    )
    return () => {
      live = false
    }
  }, [])
  return list
}
